import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from "@material-ui/core";
import api from "../../services/api";
import KoraxFormImportPreviewDialog from "../KoraxFormImportPreviewDialog";

const theme = {
  blue: "#0057FF",
  text: "#17243B",
  muted: "#718096",
  border: "#E5EAF2",
  background: "#F7F9FD"
};

const getError = error => {
  const value =
    error?.response?.data?.error ||
    error?.response?.data?.message;

  return typeof value === "string"
    ? value
    : "Não foi possível carregar as informações.";
};

const formatDate = value => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatAnswer = value => {
  if (value === null || value === undefined || value === "") {
    return "Não informado";
  }

  if (Array.isArray(value)) {
    return value.length
      ? value.map(item => String(item)).join(", ")
      : "Não informado";
  }

  if (typeof value === "boolean") {
    return value ? "Sim" : "Não";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const statusOf = request => {
  if (request.status === "submitted") {
    return {
      label: "Respondido",
      color: "#177A55",
      background: "#E7F7EF"
    };
  }

  if (
    request.expiresAt &&
    new Date(request.expiresAt).getTime() <= Date.now()
  ) {
    return {
      label: "Expirado",
      color: "#986331",
      background: "#FFF3E3"
    };
  }

  return {
    label: "Pendente",
    color: "#315CC6",
    background: "#EAF0FF"
  };
};

export default function KoraxFormHistoryDialog({
  open,
  onClose,
  onBack,
  ticketId
}) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  const [selectedId, setSelectedId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    if (!open || !ticketId) return undefined;

    let active = true;

    setRequests([]);
    setError("");
    setLoading(true);

    api.get(
      `/korax-forms/requests/ticket/${encodeURIComponent(ticketId)}`
    )
      .then(response => {
        if (!active) return;

        const items = response.data?.requests;

        setRequests(Array.isArray(items) ? items : []);
      })
      .catch(err => {
        if (active) setError(getError(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, ticketId, reload]);

  useEffect(() => {
    if (!open || !selectedId) {
      setDetail(null);
      return undefined;
    }

    let active = true;

    setDetail(null);
    setDetailError("");
    setDetailLoading(true);

    api.get(
      `/korax-forms/requests/${encodeURIComponent(selectedId)}/submission`
    )
      .then(response => {
        if (active) setDetail(response.data);
      })
      .catch(err => {
        if (active) setDetailError(getError(err));
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, selectedId]);

  const showList = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError("");
  };

  const cardStyle = {
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    background: "#FFFFFF",
    padding: 16,
    marginBottom: 12
  };

  const errorStyle = {
    background: "#FFF1F2",
    color: "#9B3347",
    padding: 13,
    borderRadius: 9,
    fontSize: 12,
    lineHeight: 1.6
  };

  if (importOpen && selectedId) {
    return (
      <KoraxFormImportPreviewDialog
        open={open}
        requestId={selectedId}
        ticketId={ticketId}
        onBack={() => setImportOpen(false)}
        onImported={() => {
          setImportOpen(false);
          setSelectedId(null);
        }}
        onClose={() => {
          setImportOpen(false);
          onClose();
        }}
      />
    );
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="korax-form-history-title"
      PaperProps={{
        style: {
          borderRadius: 15,
          color: theme.text
        }
      }}
    >
      <DialogTitle id="korax-form-history-title">
        <div style={{
          fontSize: 19,
          fontWeight: 800
        }}>
          {selectedId
            ? "Detalhes do formulário"
            : "Histórico de formulários"}
        </div>

        <div style={{
          color: theme.muted,
          fontSize: 12,
          marginTop: 6
        }}>
          {selectedId
            ? "Informações vinculadas a este atendimento."
            : "Solicitações enviadas nesta conversa."}
        </div>
      </DialogTitle>

      <DialogContent
        dividers
        style={{
          background: theme.background,
          minHeight: 230,
          maxHeight: "65vh"
        }}
      >
        {!selectedId ? (
          <>
            {loading && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: 20,
                fontSize: 13,
                color: theme.muted
              }}>
                <CircularProgress size={18} />
                Carregando histórico...
              </div>
            )}

            {error && (
              <div role="alert" style={errorStyle}>
                {error}
              </div>
            )}

            {!loading && !error && requests.length === 0 && (
              <div style={{
                textAlign: "center",
                padding: "48px 15px",
                color: theme.muted
              }}>
                <div style={{
                  fontSize: 32,
                  marginBottom: 13
                }}>
                  ▤
                </div>

                <strong style={{
                  display: "block",
                  color: theme.text,
                  marginBottom: 8
                }}>
                  Nenhum formulário enviado
                </strong>

                <span style={{ fontSize: 12 }}>
                  As solicitações desta conversa
                  aparecerão aqui.
                </span>
              </div>
            )}

            {!loading && requests.map(request => {
              const status = statusOf(request);

              return (
                <div key={request.id} style={cardStyle}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                    flexWrap: "wrap"
                  }}>
                    <div style={{
                      flex: 1,
                      minWidth: 150
                    }}>
                      <div style={{
                        fontWeight: 800,
                        fontSize: 14,
                        overflowWrap: "anywhere"
                      }}>
                        {request.titleSnapshot ||
                          "Formulário"}
                      </div>

                      <div style={{
                        color: theme.muted,
                        fontSize: 11,
                        marginTop: 6
                      }}>
                        Enviado em {formatDate(request.createdAt)}
                      </div>
                    </div>

                    <span style={{
                      padding: "6px 9px",
                      borderRadius: 20,
                      fontSize: 10,
                      fontWeight: 800,
                      color: status.color,
                      background: status.background
                    }}>
                      {status.label}
                    </span>
                  </div>

                  {request.status === "submitted" && (
                    <div style={{
                      fontSize: 11,
                      color: theme.muted,
                      marginTop: 10
                    }}>
                      Respondido em{" "}
                      {formatDate(request.submittedAt)}
                    </div>
                  )}

                  <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 13,
                    borderTop: `1px solid ${theme.border}`,
                    paddingTop: 11
                  }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedId(request.id)}
                      style={{
                        color: theme.blue,
                        fontWeight: 800,
                        textTransform: "none"
                      }}
                    >
                      {request.status === "submitted"
                        ? "Ver respostas"
                        : "Ver detalhes"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <>
            <Button
              onClick={showList}
              size="small"
              style={{
                color: theme.blue,
                textTransform: "none",
                fontWeight: 700,
                marginBottom: 15
              }}
            >
              ← Voltar ao histórico
            </Button>

            {detailLoading && (
              <div style={{
                padding: 24,
                display: "flex",
                alignItems: "center",
                gap: 12,
                color: theme.muted,
                fontSize: 13
              }}>
                <CircularProgress size={18} />
                Carregando respostas...
              </div>
            )}

            {detailError && (
              <div role="alert" style={errorStyle}>
                {detailError}
              </div>
            )}

            {detail && (
              <>
                <div style={cardStyle}>
                  <h3 style={{
                    fontSize: 17,
                    margin: "0 0 8px",
                    overflowWrap: "anywhere"
                  }}>
                    {detail.title}
                  </h3>

                  {detail.description && (
                    <p style={{
                      fontSize: 12,
                      color: theme.muted,
                      lineHeight: 1.65
                    }}>
                      {detail.description}
                    </p>
                  )}

                  <div style={{
                    color: theme.muted,
                    fontSize: 11,
                    lineHeight: 1.9
                  }}>
                    Solicitação: {formatDate(detail.createdAt)}
                    <div>
                      Resposta: {formatDate(detail.submittedAt)}
                    </div>
                  </div>
                </div>

                {detail.submission &&
                  Array.isArray(detail.fields) ? (
                  <div style={cardStyle}>
                    <h4 style={{
                      margin: "0 0 20px",
                      fontSize: 14
                    }}>
                      Respostas do cliente
                    </h4>

                    {detail.fields.map((field, index) => {
                      const answers =
                        detail.submission?.answers || {};

                      const value = Object.prototype.hasOwnProperty.call(
                        answers,
                        field.key
                      )
                        ? answers[field.key]
                        : null;

                      return (
                        <div key={`${field.key}-${index}`}
                          style={{
                            marginBottom: 17,
                            paddingBottom: 17,
                            borderBottom:
                              `1px solid ${theme.border}`
                          }}>
                          <div style={{
                            color: theme.muted,
                            fontSize: 11,
                            marginBottom: 7,
                            overflowWrap: "anywhere"
                          }}>
                            {index + 1}. {field.label}
                          </div>

                          <div style={{
                            fontWeight: 650,
                            fontSize: 13,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere"
                          }}>
                            {formatAnswer(value)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{
                    ...cardStyle,
                    fontSize: 13,
                    color: theme.muted,
                    lineHeight: 1.7
                  }}>
                    Este formulário ainda não possui
                    respostas disponíveis.
                  </div>
                )}

                <p style={{
                  color: theme.muted,
                  textAlign: "center",
                  fontSize: 11,
                  lineHeight: 1.6
                }}>
                  Visualização somente de leitura.
                  Nenhum dado será importado automaticamente.
                </p>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions style={{
        padding: "14px 19px",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between"
      }}>
        {selectedId && detail?.submission && (
          <Button
            onClick={() => setImportOpen(true)}
            style={{
              color: theme.blue,
              fontWeight: 800,
              textTransform: "none"
            }}
          >
            Preparar importação
          </Button>
        )}

        <Button
          onClick={onBack}
          style={{
            color: theme.blue,
            textTransform: "none",
            fontWeight: 700
          }}
        >
          Voltar ao envio
        </Button>

        <div>
          {!selectedId && (
            <Button
              onClick={() => setReload(value => value + 1)}
              disabled={loading}
              style={{ textTransform: "none" }}
            >
              Atualizar
            </Button>
          )}

          <Button
            onClick={onClose}
            style={{ textTransform: "none" }}
          >
            Fechar
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
